import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import * as React from 'react';
import { authClient } from '@/lib/auth-client';
import { AuthLayout } from '@/components/auth/auth-layout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/forgot-password')({
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: ForgotPasswordPage,
});

const forgotSchema = z.object({
  email: z.string().min(1, 'required').email('invalidEmail'),
});

type ForgotForm = z.infer<typeof forgotSchema>;

function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [submittedEmail, setSubmittedEmail] = React.useState('');
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [isResending, setIsResending] = React.useState(false);

  // Cooldown timer
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotForm>({
    resolver: zodResolver(forgotSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotForm) => {
    setIsSubmitting(true);
    try {
      // Better Auth endpoint: POST /api/auth/request-password-reset
      const baseURL = import.meta.env.VITE_API_BASE_URL
        ? new URL(import.meta.env.VITE_API_BASE_URL).origin
        : window.location.origin;
      await fetch(`${baseURL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: data.email, redirectTo: '/reset-password' }),
      });
      // Always show success — anti-enumeration (don't reveal if email exists)
      setSubmittedEmail(data.email);
      setSent(true);
      setResendCooldown(60);
    } catch {
      // Still show success — anti-enumeration
      setSubmittedEmail(data.email);
      setSent(true);
      setResendCooldown(60);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!submittedEmail) return;
    setIsResending(true);
    try {
      const baseURL = import.meta.env.VITE_API_BASE_URL
        ? new URL(import.meta.env.VITE_API_BASE_URL).origin
        : window.location.origin;
      await fetch(`${baseURL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: submittedEmail, redirectTo: '/reset-password' }),
      });
      setResendCooldown(60);
    } catch {
      // Silent fail — anti-enumeration
      setResendCooldown(60);
    } finally {
      setIsResending(false);
    }
  };

  const backLink = (
    <Link
      to="/login"
      className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
    >
      <ArrowLeft className="size-3.5" />
      {t('auth.backToLogin')}
    </Link>
  );

  if (sent) {
    return (
      <AuthLayout
        title={t('auth.forgotPassword.sentTitle')}
        description={t('auth.forgotPassword.sentDescription')}
        backLink={backLink}
      >
        <div className="space-y-5">
          <div className="flex items-center justify-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
              <Mail className="size-7 text-primary" />
            </div>
          </div>
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.forgotPassword.sentMessage', { email: submittedEmail })}
          </p>
          <p className="text-center text-xs text-muted-foreground/70">
            {t('auth.forgotPassword.sentHint')}
          </p>
          <Button
            variant="outline"
            className="w-full cursor-pointer"
            size="lg"
            disabled={isResending || resendCooldown > 0}
            onClick={handleResend}
          >
            {isResending ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('auth.forgotPassword.resending')}
              </>
            ) : resendCooldown > 0 ? (
              t('auth.forgotPassword.resendCooldown', { seconds: resendCooldown })
            ) : (
              t('auth.forgotPassword.resend')
            )}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.forgotPassword.title')}
      description={t('auth.forgotPassword.description')}
      backLink={backLink}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-foreground">
              {t('auth.emailLabel')}
            </Label>
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t('auth.emailPlaceholder')}
              className="h-12 rounded-lg bg-background text-sm"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              {...register('email')}
            />
            {errors.email && (
              <p id="email-error" className="text-xs text-destructive" role="alert">
                {t(`auth.validation.${errors.email.message}`)}
              </p>
            )}
          </div>
        </div>

        <Button
          type="submit"
          className="mt-8 h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
          size="lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t('auth.forgotPassword.submitting')}
            </>
          ) : (
            t('auth.forgotPassword.submit')
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
